import { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ToggleValidationButton from '../../../../components/ToggleValidationButton';
import SuccessMessageSRAMP from './components/SuccessMessageSRAMP';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Stepper, Step, StepLabel, Button, Typography, Container, Card, Divider } from '@mui/material';
import NavigateNext from '@mui/icons-material/NavigateNext';
import HeaderSession from '../../../../utils/headerSession';
import RegisterAccess from '../../../../utils/registerAccess';
import api from '../../../../services/api';

// import DadosUnidadeSaudePaciente from './stepsFichaNotificacaoProdutos/stepDadosUnidadeSaude';
import InformacaoMedicamento from './stepsFichaNotificacaoProdutos/stepInformacaoMedicamento';
import DescricaoEfeitosIndesejados from './stepsFichaNotificacaoProdutos/stepDescricaoEfeitosIndesejados';
import EvolucaoTratamento from './stepsFichaNotificacaoProdutos/stepEvolucaoTratamento'; 
import Notificador from './stepsFichaNotificacaoProdutos/stepNotificador';
import MessageError from '../../../../messages/messageError';
import LoadingShort from '../../../../load/loadingShort';


// Interface local para os props dos steps


// Tipos de ações
type ValidationAction =
  | { type: 'INIT_VALIDATION_STATE' }
  | { type: 'SHOW_VALIDATION_ERRORS'; payload: boolean }
  | { type: 'SET_STEP_ERRORS'; payload: { stepIndex: number; errors: string[] } }
  | { type: 'TOGGLE_VALIDATION_ERRORS' };

// Estado inicial
interface ValidationState {
  showValidationErrors: boolean;
  stepErrors: Record<number, string[]>;
}

const initialState: ValidationState = {
  showValidationErrors: true,
  stepErrors: {}
};

// Reducer
function validationReducer(state: ValidationState = initialState, action: ValidationAction): ValidationState {
  switch (action.type) {
    case 'INIT_VALIDATION_STATE':
      return initialState;
    case 'SHOW_VALIDATION_ERRORS':
      return { ...state, showValidationErrors: action.payload };
    case 'SET_STEP_ERRORS':
      return {
        ...state,
        stepErrors: {
          ...state.stepErrors,
          [action.payload.stepIndex]: action.payload.errors
        }
      };
    case 'TOGGLE_VALIDATION_ERRORS':
      return {
        ...state,
        showValidationErrors: !state.showValidationErrors
      };
    default:
      return state;
  }
}

interface MedicamentoData {
  nomeComercialDCI: string;
  numeroLote: string;
  doseDiaria: string;
  viaLocalAdministracao: string;
  dataCaducidade: string;
  apresentacaoDetalhes: {
    identificacaoProductoApresentacaoProductoA: boolean;
    identificacaoProductoApresentacaoProductoB: boolean;
    identificacaoProductoApresentacaoProductoC: boolean;
    identificacaoProductoApresentacaoProductoD: boolean;
    identificacaoProductoApresentacaoProductoE: boolean;
    identificacaoProductoApresentacaoProductoF: boolean;
    identificacaoProductoApresentacaoProductoG: boolean;
    identificacaoProductoApresentacaoProductoH: boolean;
    identificacaoProductoApresentacaoProductoI: boolean;
    identificacaoProductoApresentacaoProductoJ: boolean;
    identificacaoProductoApresentacaoProductoK: boolean;
    identificacaoProductoApresentacaoProductoOutros: string;
  };
  inicioTratamento: string;
  terminoTratamento: string;
  motivoTratamento: string;
  localCompra: string;
}

export default function FichaNotificacaoSRAMP() {
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [load, setLoad] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const showValidationErrors = useSelector((state: any) => state.validation?.showValidationErrors || false);

  const [messageError, setMessageError] = useState<string>('');

  // Instância única do react-hook-form para todos os steps
  const methods = useForm({
    mode: 'onChange'
  });

  useEffect(() => {
    // Garante validação ativa por padrão ao carregar o formulário
    dispatch({ type: 'SHOW_VALIDATION_ERRORS', payload: true });
  }, [dispatch]);

  const steps = [
    'Dados da Unidade de Saúde e do Paciente',
    'Informações sobre o Medicamento',
    'Descrição dos Efeitos Indesejáveis',
    'Evolução dos Efeitos Indesejaveis',// 'Evolução e Tratamento',
    'Notificador'
  ];

  // const getStepContent = (stepIndex: number) => {
  //   switch (stepIndex) {
  //     case 0:
  //       return <DadosUnidadeSaudePaciente />;
  //     case 1:
  //       return <InformacaoMedicamento />;
  //     case 2:
  //       return <DescricaoEfeitosIndesejados />;
  //     case 3:
  //       return <EvolucaoTratamento />;  //Evolução dos Efeitos Indesejaveis
  //     case 4:
  //       return <Notificador />;
  //     default:
  //       return <DadosUnidadeSaudePaciente />;
  //   }
  // };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };


  // Função de validação do step atual

  const handleNextStep = async () => {
    // Se já estiver no último step, submete em vez de avançar
    if (activeStep >= steps.length - 1) {
      await submitRecolha();
      return;
    }
    // Se validação estiver desabilitada, avança direto
    if (!showValidationErrors) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      return;
    }

    // Disparar evento personalizado para validar o step atual
    const validateEvent = new CustomEvent('validateCurrentStep', {
      detail: { stepIndex: activeStep }
    });
    let isValid = false;
    // Aguardar resposta da validação
    const validationPromise = new Promise<boolean>((resolve) => {
      const handleValidationResult = (event: Event) => {
        const customEvent = event as CustomEvent;
        resolve(customEvent.detail.isValid);
        window.removeEventListener('validationResult', handleValidationResult);
      };
      window.addEventListener('validationResult', handleValidationResult);
    });

    // problema: o evento é disparado, mas o listener não recebe a resposta da validação, ficando pendente indefinidamente. O console.log('7') é executado antes do resolve ser chamado, indicando que o código continua sem esperar pela validação. Isso sugere que o evento 'validationResult' não está sendo emitido corretamente ou que o listener não está configurado para capturá-lo. Verificar se os componentes filhos estão emitindo o evento 'validationResult' com a estrutura correta e se o listener está ativo no momento da emissão pode ajudar a resolver esse problema.
    window.dispatchEvent(validateEvent);
    // isValid = await validationPromise;
    isValid = true; // Temporariamente ignorar validação para teste
    // console.log(isValid)


    if (isValid) {
      setErrorMessage('');
      // Se próximo seria o último step, deixamos avançar normalmente
      setActiveStep((prevActiveStep) => {
        const next = prevActiveStep + 1;
        return next;
      });
    } else {
      setErrorMessage('Por favor, corrija os erros nos campos destacados.');
    }
  };

  const dadosUnidadeSaude = useSelector((state: any) => state.dadosUnidadeSaude);
  const dadosNotificador = useSelector((state: any) => state.dadosNotificador);
  const dadosEfeitos = useSelector((state: any) => state.dadosEfeitosIndesejados);
  const dadosEvolucao = useSelector((state: any) => state.dadosEvolucaoTratamento);
  const informacoesMedicamento = useSelector((state: any) => state.informacoesMedicamento);
  const dadosPaciente = useSelector((state: any) => state.dadosPaciente);

  const validateStep = (_stepIndex: number): boolean => {
    return true;
  };


  // console.log(dadosEfeitos)
  const submitRecolha = async () => {
    // Valida o step Notificador antes de enviar
    const isNotifierValid = await methods.trigger([
      'nomeApelidoNotificador',
      'categoriaProfissional',
      'telefone',
      'email'
    ]);
    // console.log('[submitRecolha] Notificador valid?', isNotifierValid);
    if (!isNotifierValid) {
      setErrorMessage('Por favor, corrija os erros nos campos destacados.');
      setActiveStep(4); // Garantir que o step Notificador esteja visível
      return;
    }

    // Mapeia opções para enums esperados pela API: "Sim" | "Não"
    // Aceita variações com/sem acento e capitalização diferente
    const mapOpcaoEnum = (v?: string) => {
      if (!v) return undefined;
      const norm = v
        .toString()
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, ''); // remove acentos
      if (norm === 'sim') return 'Sim';
      if (norm === 'nao' || norm === 'não') return 'Não';
      return undefined;
    };


    try {
      setLoad(true);
      setMessageError('');
      // console.log(dadosEfeitos)

      const payload = {
        unidadeSanitariaNome: String(dadosUnidadeSaude.unidadeSanitariaNome),
        unidadeSanitariaProvincia: String(dadosUnidadeSaude.unidadeSanitariaProvincia),
        unidadeSanitariaMunicipio: String(dadosUnidadeSaude.unidadeSanitariaMunicipio),
        unidadeSanitariaBairro: String(dadosUnidadeSaude.unidadeSanitariaBairro),
        unidadeSanitariaServico: String(dadosUnidadeSaude.unidadeSanitariaServico),
        unidadeSanitariaRua: String(dadosUnidadeSaude.unidadeSanitariaRua),
        unidadeSanitariaEmail: String(dadosUnidadeSaude.unidadeSanitariaEmail),

        automedicacao: !!dadosEfeitos?.tipoOcorrencia?.automedicacao,
        farmacodependencia: !!dadosEfeitos?.tipoOcorrencia?.farmacodependencia,
        erroTerapeutico: !!dadosEfeitos?.tipoOcorrencia?.erroTerapeutico,
        campanhaVacinacao: !!dadosEfeitos?.tipoOcorrencia?.campanhaVacinacao,
        conducoesArmazenamento: !!informacoesMedicamento?.conducoesArmazenamento,
        pacienteNome: String(dadosUnidadeSaude.pacienteNome),
        pacienteIdade: String(dadosUnidadeSaude.pacienteIdade),
        pacienteSexo: String(dadosUnidadeSaude.pacienteSexo),
        pacienteProvincia: String(dadosUnidadeSaude.pacienteProvincia),
        pacienteMunicipio: String(dadosUnidadeSaude.pacienteMunicipio),
        pacienteBairro: String(dadosUnidadeSaude.pacienteBairro),
        pacienteEmail: String(dadosUnidadeSaude.pacienteEmail),
        pacienteTelefone: String(dadosUnidadeSaude.pacienteTelefone),
        pacientePeso: dadosUnidadeSaude.pacientePeso ? Number(dadosUnidadeSaude.pacientePeso) : '',
        pacienteAltura: dadosUnidadeSaude.pacienteAltura ? Number(dadosUnidadeSaude.pacienteAltura) : '',
        pacienteGestante: !!dadosUnidadeSaude.pacienteGestante,
        pacienteRaca: String(dadosUnidadeSaude.pacienteRaca),
        pacienteAntecendentesMedicosSP: !!dadosUnidadeSaude.pacienteAntecendentesMedicosSP,
        pacienteAntecendentesReaccoesAMO: !!dadosUnidadeSaude.pacienteAntecendentesReaccoesAMO,
        pacienteAntecendentesCirurgicos: !!dadosUnidadeSaude.pacienteAntecendentesCirurgicos,
        pacienteAntecendentesAbusoDependencia: !!dadosUnidadeSaude.pacienteAntecendentesAbusoDependencia,
        pacienteAntecendentesAlergiasConhecidas: !!dadosUnidadeSaude.pacienteAntecendentesAlergiasConhecidas,
        efeitosIndesejadosDescricao: String(dadosEfeitos?.breveDescricao),
        // efeitosIndesejadosInicioManifestacao: String(dadosEfeitos?.prazoManifestacao?.inicioManifestacao),
        // efeitosIndesejadosFimManifestacao: String(dadosEfeitos?.prazoManifestacao?.fimManifestacao),
        efeitosIndesejadosInicioManifestacao: dadosEfeitos?.prazoManifestacaoInicioManifestacao ?? '',
        efeitosIndesejadosFimManifestacao: dadosEfeitos?.prazoManifestacaoFimManifestacao ?? '',
        efeitosIndesejadosEvolucaoEfeitosRiscoVida: !!dadosEvolucao?.evolucaoEfeitos?.riscoVida,
        efeitosIndesejadosEvolucaoEfeitosMotivouHospitalizacao: !!dadosEvolucao?.evolucaoEfeitos?.motivouHospitalizacao,
        efeitosIndesejadosEvolucaoEfeitosProlongouHospitalizacao: !!dadosEvolucao?.evolucaoEfeitos?.prolongouHospitalizacao,
        efeitosIndesejadosEvolucaoEfeitosNaoRecuperouAind: !!dadosEvolucao?.evolucaoEfeitos?.naoRecuperado,
        efeitosIndesejadosEvolucaoEfeitosRecuperouSemSequelas: !!dadosEvolucao?.evolucaoEfeitos?.recuperacaoSemSequelas,
        efeitosIndesejadosEvolucaoEfeitosRecuperouComSequelas: !!dadosEvolucao?.evolucaoEfeitos?.recuperacaoComSequelas,
        efeitosIndesejadosEvolucaoEfeitosDesconhecido: !!dadosEvolucao?.evolucaoEfeitos?.desconhecido,
        efeitosIndesejadosEvolucaoEfeitosMorte: !!dadosEvolucao?.evolucaoEfeitos?.morte,
        efeitosIndesejadosEvolucaoEfeitosTratamentoCorrector: dadosEvolucao?.tratamentoCorretor ? String(dadosEvolucao?.tratamentoCorretor) : undefined,
        efeitosIndesejadosEvolucaoEfeitosSuspendeuMedicamento: dadosEvolucao?.suspendeuMedicamento,
        efeitosIndesejadosEvolucaoEfeitosDiminuiuDoseMedicamento: dadosEvolucao?.diminuiuDose,
        efeitosIndesejadosEvolucaoEfeitosEfeitoIDDASDM: dadosEvolucao?.efeitoDesapareceuSuspensao, // verificar bem depoi
        efeitosIndesejadosEvolucaoEfeitosEfeitoIRARDM: dadosEvolucao?.efeitoReapareceuReintroducao,
        efeitosIndesejadosEvolucaoEfeitosMalformacaoCongenita: !!dadosEvolucao?.evolucaoEfeitos?.malformacaoCongenita,
        notificadorNome: dadosNotificador.nomeApelidoNotificador,
        notificadorCategoriaProfissional: dadosNotificador.categoriaProfissional,
        notificadorTel: dadosNotificador.telefone,
        notificadorEmail: dadosNotificador.email,
        dataNotificacao: dadosNotificador.dataNotificacao,

        anexos: informacoesMedicamento?.anexos || []
      };

      // setLoad(true);
      // const formData = new FormData();
      // formData.append("unidadeSanitariaNome", String(dadosUnidadeSaude.unidadeSanitariaNome))
      // formData.append("unidadeSanitariaProvincia", String(dadosUnidadeSaude.unidadeSanitariaProvincia))
      // formData.append("unidadeSanitariaMunicipio", String(dadosUnidadeSaude.unidadeSanitariaMunicipio))
      // formData.append("unidadeSanitariaBairro", String(dadosUnidadeSaude.unidadeSanitariaBairro))
      // formData.append("unidadeSanitariaServico", String(dadosUnidadeSaude.unidadeSanitariaServico))
      // formData.append("unidadeSanitariaRua", String(dadosUnidadeSaude.unidadeSanitariaRua))
      // formData.append("unidadeSanitariaEmail", String(dadosUnidadeSaude.unidadeSanitariaEmail))

      // formData.append("automedicacao", !!dadosEfeitos?.tipoOcorrencia?.automedicacao ? 'Sim' : 'Não')
      // formData.append("farmacodependencia", !!dadosEfeitos?.tipoOcorrencia?.farmacodependencia ? 'Sim' : 'Não')
      // formData.append("erroTerapeutico", !!dadosEfeitos?.tipoOcorrencia?.erroTerapeutico ? 'Sim' : 'Não')
      // formData.append("campanhaVacinacao", !!dadosEfeitos?.tipoOcorrencia?.campanhaVacinacao ? 'Sim' : 'Não')

      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, v]) => v !== undefined && v !== '' && v !== null)
      );

      // Novo payload para identificação do produto
      let farmacoVigilanciaId: string | undefined;

      // Envia o payload principal e obtém o ID da resposta
      const responsePrincipal = await api.post('/farmaco-vigilancia/create/NRAMPS', filteredPayload);
      // console.log(responsePrincipal);
      farmacoVigilanciaId = responsePrincipal?.data?.farmacoVigilancia?._id || responsePrincipal?.data?.id || responsePrincipal?.data?._id;

      setLoad(false);
      if (!farmacoVigilanciaId) {
        alert('Erro: farmacoVigilanciaId não foi retornado pela API. Verifique o backend ou o payload enviado.');
        return;
      }

      // Filtra medicamentos preenchidos
      const medicamentosPreenchidos = informacoesMedicamento?.medicamentos?.filter((m: MedicamentoData) => m.nomeComercialDCI && m.nomeComercialDCI.trim() !== '');
      if (!medicamentosPreenchidos || medicamentosPreenchidos.length === 0) {
        alert('Preencha pelo menos um medicamento com Nome Comercial/DCI para enviar.');
        // setLoad(true);
        return;
      }
      const med = medicamentosPreenchidos[0];
      const produtoPayload = {
        farmacoVigilanciaId,
        identificacaoProductoNomeDCI: med.nomeComercialDCI,
        identificacaoProductoNumeroLote: med.numeroLote || undefined,
        identificacaoProductoDoseDiaria: med.doseDiaria || undefined,
        identificacaoProductoViaLocalAdmin: med.viaLocalAdministracao || undefined,
        identificacaoProductoInicioDateToma: med.inicioTratamento || undefined,
        identificacaoProductoTerminoDateToma: med.terminoTratamento || undefined,
        identificacaoProductoMotivoTratamento: med.motivoTratamento || undefined,
        identificacaoProductoLocalCompra: med.localCompra || undefined,
        identificacaoProductoDataExpiracao: med.dataCaducidade || undefined,
        identificacaoProductoApresentacaoProductoA: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoA === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoA : undefined,
        identificacaoProductoApresentacaoProductoB: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoB === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoB : undefined,
        identificacaoProductoApresentacaoProductoC: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoC === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoC : undefined,
        identificacaoProductoApresentacaoProductoD: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoD === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoD : undefined,
        identificacaoProductoApresentacaoProductoE: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoE === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoE : undefined,
        identificacaoProductoApresentacaoProductoF: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoF === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoF : undefined,
        identificacaoProductoApresentacaoProductoG: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoG === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoG : undefined,
        identificacaoProductoApresentacaoProductoH: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoH === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoH : undefined,
        identificacaoProductoApresentacaoProductoI: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoI === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoI : undefined,
        identificacaoProductoApresentacaoProductoJ: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoJ === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoJ : undefined,
        identificacaoProductoApresentacaoProductoK: typeof med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoK === 'boolean' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoK : undefined,
        identificacaoProductoApresentacaoProductoOutros: med?.apresentacaoDetalhes?.identificacaoProductoApresentacaoProductoOutros && med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoOutros.trim() !== '' ? med.apresentacaoDetalhes.identificacaoProductoApresentacaoProductoOutros : undefined // só envia se tiver valor preenchido, caso contrário undefined para não incluir no payload
      };

      // Envia o produto após obter o ID
      const responseProduto = await api.patch('/farmaco-vigilancia/NRAMPS/add-producto', produtoPayload);

      // Sucesso: limpar estados e mostrar mensagem
      setShowSuccess(true);
      setLoad(true);
      // Reset Redux slices específicos da farmacovigilância
      dispatch({ type: 'RESET_FARMACOVIGILANCIA' });
      // Reset do formulário RHF e voltar para o primeiro passo
      methods.reset();
      setActiveStep(0);
    } catch (error: any) {
      setLoad(false);
      console.log(error);
      setMessageError(error?.response?.data?.message || 'Ocorreu um erro ao enviar a notificação. Tente novamente.');
    }
  };

  // para monitorar mudanças na mensagem de erro e ajustar a mensagem exibida ao usuário
  useEffect(() => {

    console.log('Mensagem de erro atualizada:', errorMessage);

    messageError === 'O campo efeitosIndesejadosEvolucaoEfeitosSuspendeuMedicamento é obrigatório' && setMessageError('Por favor, preencha se o medicamento foi suspenso ou não para continuar.');
    messageError === 'O campo efeitosIndesejadosDescricao é obrigatório' && setMessageError('Por favor, preencha a descrição dos efeitos indesejados para continuar.');
    messageError === 'O campo efeitosIndesejadosEvolucaoEfeitosDiminuiuDoseMedicamento é obrigatório' && setMessageError('Por favor, preencha se a dose do medicamento foi diminuída ou não para continuar.');

    messageError === 'O campo efeitosIndesejadosEvolucaoEfeitosEfeitoIRARDM é obrigatório' && setMessageError('Por favor, preencha se o efeito reapareceu com a reintrodução do medicamento para continuar.');
    messageError === 'O campo efeitosIndesejadosEvolucaoEfeitosEfeitoIDDASDM é obrigatório' && setMessageError('Por favor, preencha se o efeito desapareceu com a suspensão do medicamento para continuar.');
    messageError === 'O campo efeitosIndesejadosEvolucaoEfeitosRiscoVida é obrigatório' && setMessageError('Por favor, preencha se os efeitos indesejados apresentaram risco de vida para continuar.');
    messageError === 'O campo efeitosIndesejadosEvolucaoEfeitosMotivouHospitalizacao é obrigatório' && setMessageError('Por favor, preencha se os efeitos indesejados motivaram hospitalização para continuar.');
    messageError === 'O campo efeitosIndesejadosEvolucaoEfeitosProlongouHospitalizacao é obrigatório' && setMessageError('Por favor, preencha se os efeitos indesejados prolongaram a hospitalização para continuar.');
    messageError === 'O campo efeitosIndesejadosEvolucaoEfeitosNaoRecuperouAind é obrigatório' && setMessageError('Por favor, preencha se os efeitos indesejados não recuperaram até o momento para continuar.');
    messageError === 'O campo efeitosIndesejadosEvolucaoEfeitosRecuperouSemSequelas é obrigatório' && setMessageError('Por favor, preencha se os efeitos indesejados recuperaram sem sequelas para continuar.');
    messageError === 'O campo efeitosIndesejadosEvolucaoEfeitosRecuperouComSequelas é obrigatório' && setMessageError('Por favor, preencha se os efeitos indesejados recuperaram com sequelas para continuar.');
    messageError === 'O campo efeitosIndesejadosEvolucaoEfeitosDesconhecido é obrigatório' && setMessageError('Por favor, preencha se a evolução dos efeitos indesejados é desconhecida para continuar.');
    messageError === 'O campo efeitosIndesejadosEvolucaoEfeitosMorte é obrigatório' && setMessageError('Por favor, preencha se os efeitos indesejados resultaram em morte para continuar.');
    messageError === 'O campo efeitosIndesejadosInicioManifestacao é obrigatório' && setMessageError('Por favor, preencha a data de início da manifestação dos efeitos indesejados para continuar.');
    messageError === 'O campo efeitosIndesejadosFimManifestacao é obrigatório' && setMessageError('Por favor, preencha a data de fim da manifestação dos efeitos indesejados para continuar.');


    messageError === 'O campo pacienteNome é obrigatório' && setMessageError('Por favor, preencha o nome do paciente para continuar.');
    messageError === 'O campo pacienteIdade é obrigatório' && setMessageError('Por favor, preencha a idade do paciente para continuar.');
    messageError === 'O campo pacienteSexo é obrigatório' && setMessageError('Por favor, preencha o sexo do paciente para continuar.');
    messageError === 'O campo pacienteProvincia é obrigatório' && setMessageError('Por favor, preencha a província do paciente para continuar.');
    messageError === 'O campo pacienteMunicipio é obrigatório' && setMessageError('Por favor, preencha o município do paciente para continuar.');
    messageError === 'O campo pacienteBairro é obrigatório' && setMessageError('Por favor, preencha o bairro do paciente para continuar.');
    messageError === 'O campo pacienteEmail é obrigatório' && setMessageError('Por favor, preencha o email do paciente para continuar.');
    messageError === 'O campo pacienteTelefone é obrigatório' && setMessageError('Por favor, preencha o telefone do paciente para continuar.');
    messageError === 'O campo pacientePeso é obrigatório' && setMessageError('Por favor, preencha o peso do paciente para continuar.');
    messageError === 'O campo pacienteAltura é obrigatório' && setMessageError('Por favor, preencha a altura do paciente para continuar.');
    messageError === 'O campo pacienteGestante é obrigatório' && setMessageError('Por favor, preencha se o paciente é gestante ou não para continuar.');
    messageError === 'O campo pacienteRaca é obrigatório' && setMessageError('Por favor, preencha a raça do paciente para continuar.');
    messageError === 'O campo pacienteAntecendentesMedicosSP é obrigatório' && setMessageError('Por favor, preencha se o paciente tem antecedentes médicos de saúde pública para continuar.');
    messageError === 'O campo pacienteAntecendentesReaccoesAMO é obrigatório' && setMessageError('Por favor, preencha se o paciente tem antecedentes de reações adversas a medicamentos ou outros para continuar.');
    messageError === 'O campo pacienteAntecendentesCirurgicos é obrigatório' && setMessageError('Por favor, preencha se o paciente tem antecedentes cirúrgicos para continuar.');
    messageError === 'O campo pacienteAntecendentesAbusoDependencia é obrigatório' && setMessageError('Por favor, preencha se o paciente tem antecedentes de abuso ou dependência de substâncias para continuar.');
    messageError === 'O campo pacienteAntecendentesAlergiasConhecidas é obrigatório' && setMessageError('Por favor, preencha se o paciente tem antecedentes de alergias conhecidas para continuar.');

    messageError === 'O campo notificadorNome é obrigatório' && setMessageError('Por favor, preencha o nome do notificador para continuar.');
    messageError === 'O campo notificadorCategoriaProfissional é obrigatório' && setMessageError('Por favor, preencha a categoria profissional do notificador para continuar.');
    messageError === 'O campo notificadorTel é obrigatório' && setMessageError('Por favor, preencha o telefone do notificador para continuar.');
    messageError === 'O campo notificadorEmail é obrigatório' && setMessageError('Por favor, preencha o email do notificador para continuar.');

    messageError === 'O campo unidadeSanitariaNome é obrigatório' && setMessageError('Por favor, preencha o nome da unidade de saúde para continuar.');
    messageError === 'O campo unidadeSanitariaEmail é obrigatório' && setMessageError('Por favor, preencha o email da unidade de saúde para continuar.');
    messageError === 'O campo unidadeSanitariaProvincia é obrigatório' && setMessageError('Por favor, preencha a província da unidade de saúde para continuar.');
    messageError === 'O campo unidadeSanitariaBairro é obrigatório' && setMessageError('Por favor, preencha o bairro da unidade de saúde para continuar.');
    messageError === 'O campo unidadeSanitariaMunicipio é obrigatório' && setMessageError('Por favor, preencha o município da unidade de saúde para continuar.');
    messageError === 'O campo unidadeSanitariaRua é obrigatório' && setMessageError('Por favor, preencha a rua da unidade de saúde para continuar.');
    messageError === 'O campo unidadeSanitariaServico é obrigatório' && setMessageError('Por favor, preencha o serviço da unidade de saúde para continuar.');

    messageError === 'O campo conducoesArmazenamento é obrigatório' && setMessageError('Por favor, preencha se houve falhas nas condições de armazenamento do medicamento para continuar.');
    messageError === 'O campo erroTerapeutico é obrigatório' && setMessageError('Por favor, preencha se houve erro terapêutico para continuar.');
    messageError === 'O campo campanhaVacinacao é obrigatório' && setMessageError('Por favor, preencha se a ocorrência está relacionada a uma campanha de vacinação para continuar.');
    messageError === 'O campo farmacodependencia é obrigatório' && setMessageError('Por favor, preencha se houve farmacodependência para continuar.');
    messageError === 'O campo automedicacao é obrigatório' && setMessageError('Por favor, preencha se houve automedicação para continuar.');
    messageError === 'O campo efeitosIndesejadosEvolucaoEfeitosMalformacaoCongenita é obrigatório' && setMessageError('Por favor, preencha se os efeitos indesejados resultaram em malformação congênita para continuar.');

  }, [messageError]);

  return (
    <Container style={{ marginBottom: 12, minHeight: "70vh" }}>
      {showSuccess ? (
        <SuccessMessageSRAMP message="Notificação enviada com sucesso!" onClose={() => setShowSuccess(false)} />
      ) : (
        <>
          {/* <ToggleValidationButton /> */}
          <RegisterAccess page={'ficha de notificacoes de suspeita de reacções adversas a medicamentos'} />
          <HeaderSession title='NOTIFICAÇÃO DE SUSPEITA DE REACÇÕES ADVERSAS A MEDICAMENTOS E PRODUTOS SANITÁRIOS' />

          {messageError && <MessageError message={messageError} />}

          <Card style={{ marginBottom: 12, padding: 22 }}>
            <FormProvider {...methods}>
              <form>

                <div style={{ overflow: 'auto' }}>
                  <Stepper color="secondary" activeStep={activeStep} alternativeLabel>
                    {steps.map((label) => (
                      <Step key={label} >
                        <StepLabel style={{ color: '#85287e' }}>
                          {label}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                  <Divider />
                </div>

                {activeStep === 0 && (''
                  // <DadosUnidadeSaudePaciente />
                )}
                {activeStep === 1 && (
                  <InformacaoMedicamento />
                )}
                {activeStep === 2 && (
                  <DescricaoEfeitosIndesejados />
                )}
                {activeStep === 3 && (
                  <EvolucaoTratamento />
                )}
                {activeStep === 4 && (
                  <Notificador />
                )}

                {/* Exibir mensagens de erro de validação */}
                {errorMessage && (
                  <div style={{
                    backgroundColor: '#ffebee',
                    color: '#c62828',
                    padding: '16px',
                    margin: '16px 0',
                    borderRadius: '4px',
                    border: '1px solid #ef5350'
                  }}>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                      {errorMessage}
                    </Typography>
                  </div>
                )}

                <div style={{ display: 'flex', margin: '20px 10px' }}>
                  <Button
                    style={{ background: '#ebebf4' }}
                    variant="outlined"
                    disabled={activeStep === 0}
                    onClick={handleBack}
                  >
                    Voltar
                  </Button>
                  <Box style={{ display: 'flex', flexGrow: 1 }} />
                  {activeStep === steps.length - 1 ? (
                    <Button
                      style={{ background: '#85287e', color: 'white', width: 120 }}
                      variant="contained"
                      onClick={submitRecolha}
                    >
                      {load ? <LoadingShort text={''} /> : 'Enviar'}

                    </Button>
                  ) : (
                    <Button
                      style={{ background: '#85287e', color: 'white', width: 120 }}
                      variant="contained"
                      onClick={handleNextStep}
                    >
                      Próximo <NavigateNext />
                    </Button>
                  )}
                </div>
              </form>
            </FormProvider>
          </Card>
        </>
      )}
    </Container>
  );
}